import React, { useEffect, useState } from "react";
import { ToggleButtons, ToggleButton } from "@emisgroup/ui-toggle-buttons";
import { Bands, BandsTypes } from "../../Banding/Types";
import { Capitalize } from "../../Banding/Utilities";
import { OutputMappingChild, OutputMappingItem } from "../OutputMappingList/Types";
import { FilterChildrensBasedOnBand } from "../Script";
import { OutputMappingChildCard } from "../OutputMappingChildCard/OutputMappingChildCard.logic";
import { ToggleButtonItem } from "../OutputMappingChildCard/Data";
import { ButtonVariantType } from "../../../../Common/UI/Types";
import { Button } from "../../../../Common/UI/Index";
import TriangleUp from "~icons/ic/outline-arrow-drop-up";
import TriangleDown from "~icons/ic/outline-arrow-drop-down";
import "../OutputMappingChildCard/Styles.scss";
import "./Styles.scss";

interface Props {
  /**
   * Child item details
   */
  mappingChildItem: OutputMappingItem | OutputMappingChild;
  /**
   * Layer id in render order
   */
  layerId: number;
  /**
   * On Toggle select
   */
  onToggleButtonSelect: (value: number) => void;
  /**
   * Selected Toggle
   */
  selectedToggle: number;
  /**
   * Flag to set toggle
   */
  show?: boolean;/**
   * Pass band type to previous child
   * @param value Band type
   * @returns
   */
  passToPrev: (value: string) => void;
}

/**
 * Responsible to render Output Mapping Child Card view for bandings
 * @returns Output Mapping Child Card view
 */
export const OutputMappingChildCardsForBandingsView: React.FC<Props> = (props: Props) => {
  const {
    mappingChildItem,
    layerId,
    onToggleButtonSelect,
    selectedToggle,
    show,
    passToPrev
  } = props;
  const child = mappingChildItem?.childrenOutputElements as OutputMappingChild[];

  const ageBands = FilterChildrensBasedOnBand(child, BandsTypes.AGE.toUpperCase()) || [];
  const genderBands = FilterChildrensBasedOnBand(child, BandsTypes.GENDER.toUpperCase());
  const ethnicityBands = FilterChildrensBasedOnBand(child, BandsTypes.ETHNICITY.toUpperCase());

  const [toggleView, setToggleView] = useState(show);
  const isBandingAvailale = ((ageBands && ageBands.length > 0) ||
    (genderBands && genderBands.length > 0) ||
    (ethnicityBands && ethnicityBands.length > 0));

  const [viewToggle, setViewToggle] = useState(0);
  const [data, setData] = useState<ToggleButtonItem[]>([]);
  const [isSelectedBandingEmpty, setIsSelectedBandingEmpty] = useState(true);
  const [bandFromChild, setBandFromChild] = useState("");
  const [searchToggle, setSearchToggle] = useState(selectedToggle);
  const [ageToggle, setAgeToggle] = useState(0);
  const [genderToggle, setGenderToggle] = useState(0);
  const [ethnicityToggle, setEthnicityToggle] = useState(0);

  /**
   * Update when child or toggle changes
   */
  useEffect(() => {
    if (isBandingAvailale) {
      const isAgeBandingAvailable = ageBands && ageBands.length > 0;
      const isGenderBandingAvailable = genderBands && genderBands.length > 0;
      const isEthnicityBandingAvailable = ethnicityBands && ethnicityBands.length > 0;

      const localData: ToggleButtonItem[] = [];
      let increment = selectedToggle;
      /**
       * Set toggle button list based on bands availability
       */
      if (isAgeBandingAvailable) {
        localData.push({
          ariaControls: BandsTypes.AGE,
          text: BandsTypes.AGE + " (" + ageBands.length + ")"
        });
        setAgeToggle(increment);
        increment++;
      }
      if (isGenderBandingAvailable) {
        localData.push({
          ariaControls: BandsTypes.GENDER,
          text: BandsTypes.GENDER + " (" + genderBands.length + ")"
        });
        setGenderToggle(increment);
        increment++;
      }
      if (isEthnicityBandingAvailable) {
        localData.push({
          ariaControls: BandsTypes.ETHNICITY,
          text: BandsTypes.ETHNICITY + " (" + ethnicityBands.length + ")"
        });
        setEthnicityToggle(increment);
      }
      setData([...localData]);
    }
  }, [child, selectedToggle]);

  /**
   * Update view once data changes
   */
  useEffect(() => {
    if (data && data[selectedToggle]) {
      const view = data[selectedToggle].text;
      if (view === BandsTypes.AGE + " (" + ageBands.length + ")") {
        setViewToggle(Bands.AGE);
      }
      else if (view === BandsTypes.GENDER + " (" + genderBands.length + ")") {
        setViewToggle(Bands.GENDER);
      }
      else if (view === BandsTypes.ETHNICITY + " (" + ethnicityBands.length + ")") {
        setViewToggle(Bands.ETHNICITY);
      }
      setIsSelectedBandingEmpty(false);
    }
  }, [data]);

  /**
   * Toggle view
   */
  const onToggleView = (): void => {
    setToggleView(!toggleView);
  };

  const passToParent = (value: string): void => {
    setBandFromChild(Capitalize(value.toLowerCase()));
  };

  /**
   * Action when band is passed from child
   */
  useEffect(() => {
    if (bandFromChild !== "") {
      if (bandFromChild === BandsTypes.AGE) {
        setSearchToggle(ageToggle);
        onToggleButtonSelect(ageToggle);
        setViewToggle(Bands.AGE);
      }
      else if (bandFromChild === BandsTypes.GENDER) {
        setSearchToggle(genderToggle);
        onToggleButtonSelect(genderToggle);
        setViewToggle(Bands.GENDER);
      }
      else if (bandFromChild === BandsTypes.ETHNICITY) {
        setSearchToggle(ethnicityToggle);
        onToggleButtonSelect(ethnicityToggle);
        setViewToggle(Bands.ETHNICITY);
      }
      passToPrev(mappingChildItem.bandType);
    }
  }, [bandFromChild]);

  const handleToggleChange = (value: string): void => {
    onToggleButtonSelect(Bands[value]);
  };

  return (
    <div
      className={`${layerId === 1 && "first-child-item"}`}
    >
      {
        (isBandingAvailale) &&
        (
          <>
            <div className="output-config__collapse_btn_holder">
              <div
                className="output-config__collapse_btn"
                id="ebt-outputConfig-config-collapsebtn"
              >
                <Button
                  variant={ButtonVariantType.FILLED}
                  onClick={onToggleView}
                >
                  {
                    toggleView ?
                      <TriangleUp title="Toggle up" /> :
                      <TriangleDown title="Toggle down" />
                  }
                </Button>
              </div>
              <ToggleButtons
                aria-label="output config tabs"
                type="single"
                data-testid="output-config-childcard-toggle-buttons"
                id="ebt-outputConfig-config-bandingbtn"
                onValueChange={(value): void => handleToggleChange(value)}
                value={data?.[searchToggle]?.ariaControls}
              >
                {data?.map((tabs: ToggleButtonItem, index: number) => {
                  return (
                    <ToggleButton
                      aria-controls={tabs?.ariaControls}
                      value={tabs?.ariaControls}
                      key={tabs?.text}
                      onClick={(): void => onToggleButtonSelect(index)}
                    >
                      {tabs?.text}
                    </ToggleButton>
                  );
                })}
              </ToggleButtons>
            </div>
            {
              toggleView &&
              !isSelectedBandingEmpty &&
              <div className="childStyle-1"
                id="ebt-outputConfig-config-childConfig"
              >
                <div className={viewToggle !== Bands.AGE ? "hidden-item" : ""}>
                  {
                    ageBands?.map((item: OutputMappingChild) => (
                      <>
                        <OutputMappingChildCard
                          mappingChildItem={item}
                          key={item.configName}
                          layerId={1}
                          passToParent={passToParent}
                        />
                      </>
                    ))
                  }
                </div>
                <div className={viewToggle !== Bands.GENDER ? "hidden-item" : ""}>
                  {
                    genderBands?.map((item: OutputMappingChild) => (
                      <>
                        <OutputMappingChildCard
                          mappingChildItem={item}
                          key={item.configName}
                          layerId={1}
                          passToParent={passToParent}
                        />
                      </>
                    ))
                  }
                </div>
                <div className={viewToggle !== Bands.ETHNICITY ? "hidden-item" : ""}>
                  {
                    ethnicityBands?.map((item: OutputMappingChild) => (
                      <>
                        <OutputMappingChildCard
                          mappingChildItem={item}
                          key={item.configName}
                          layerId={1}
                          passToParent={passToParent}
                        />
                      </>
                    ))
                  }
                </div>
              </div>
            }
          </>
        )
      }
    </div>
  );
};
